import { FileField, RequestFile, RequestParticipant } from "models/interfaces";
import { fieldsRemaining, SignerState } from "./ParticipantScreenService";
import { isTruthy } from "helpers/methods";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "hooks/use-app-select";
import { useRef, useState, useCallback, useEffect } from "react";
import { selectScreen } from "redux/appSlice";
import { DEFAULT_HEIGHT_PX, DEFAULT_WIDTH_PX } from "../SignRequestHelper";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SignatureFields from "./Fields/SignatureFields";
import CustomTextFieldsElement from "./Fields/CustomTextFieldsElement";
import CustomRadioFieldElement from "./Fields/CustomRadioFieldElement";
import CustomCheckBoxFieldsElement from "./Fields/CustomCheckBoxFieldsElement";
import CustomDateFieldElement from "./Fields/CustomDateFieldElement";
import CustomAttachmentFieldElement from "./Fields/CustomAttachmentFieldElement";
import CustomDropDownFieldElement from "./Fields/CustomDropDownFieldElement";

const ViewFields = (props: {
  state: SignerState;
  field: any;
  dispatch: Function;
  selectedFile: RequestFile;
  setSelectedFile: Function;
  setLoading: Function;
  signer: RequestParticipant;
  currentReviewer: RequestParticipant;
  signImage: string;
  initialsImage: string;
  currentPageNumber: number;
  frameX: number;
  frameY: number;
  currentFieldOnFocus: number;
  setCurrentFieldOnFocus: Function;
  moveToFieldLocation: Function;
  signingJourneyOn: boolean;
  pagesHeight: any;
  setDocLoading: Function;
  isCurrentField: any;
  isMobileScreen?: boolean;
  disableFields?: boolean;
}) => {
  const theme = useTheme();
  const $el = useRef(null);
  const isMobile = useAppSelector(selectScreen);
  const [coords, setCoords] = useState({
    pageX: 0,
    pageY: 0,
    pageHeight: DEFAULT_HEIGHT_PX || 0,
    pageWidth: DEFAULT_WIDTH_PX || 0,
    width: 0,
    height: 0,
  });
  const pageDims = props.selectedFile?.dimensions?.[props.field.page];
  const ry = pageDims?.y ? coords.height / pageDims.y : 1;

  const calculateCoords = useCallback(() => {
    const isText = [
      strings.text,
      strings.dropDown,
      strings.email,
      strings.name,
      strings.title,
      strings.companyName,
      strings.dateSigned,
    ].includes(props.field.type);
    const $page = document.querySelector<HTMLElement>(
      `.react-pdf__Page[data-page-number="${props.field.page + 1}"]`,
    );

    if (!$page) return;

    const { height, width } = $page.getBoundingClientRect();

    // Horizontal origin = the page's real left offset within the overlay's
    // positioning parent (they share an offsetParent). Using the measured
    // offset instead of a hardcoded ~22px keeps fields aligned regardless of
    // how the page is centered — the hardcoded value drifted fields right in
    // the signing view vs where they were placed in the editor.
    const pageX =
      $page.offsetLeft +
      (props.field.fieldPageX / 100) * width +
      (props.field.type === strings.checkbox && isMobile ? -4 : 0);
    const pageY =
      (props.field.fieldPageY / 100) * height +
      props.field.page * height +
      (props.field.type === strings.checkbox && isMobile ? -8 : 1);

    const pageWidth = (props.field.fieldPageWidth / 100) * width;
    const pageHeight =
      (props.field.fieldPageHeight / 100) * height - (isText ? 2 : 0);
    setCoords({
      pageX: pageX,
      pageY: pageY,
      pageHeight: pageHeight,
      pageWidth: pageWidth,
      width: width,
      height: height,
    });

    setCoords((prevCoords) => {
      if (
        prevCoords.pageX !== pageX ||
        prevCoords.pageY !== pageY ||
        prevCoords.pageWidth !== pageWidth ||
        prevCoords.pageHeight !== pageHeight
      ) {
        return { pageX, pageY, pageWidth, pageHeight, width, height };
      }
      return prevCoords;
    });
  }, [props.field]);

  const calculateExitingCoords = useCallback(() => {
    const $page = document.querySelector<HTMLElement>(
      `.react-pdf__Page[data-page-number="${props.field.page + 1}"]`,
    );

    if (!$page) return;

    const exitingPageDims = props.selectedFile?.dimensions?.[props.field.page];
    if (!exitingPageDims?.x || !exitingPageDims?.y) return;

    const { height, width } = $page.getBoundingClientRect();

    // See calculateCoords: use the page's measured left offset as the origin
    // instead of a hardcoded ~18px, so fields don't drift horizontally.
    const pageX = $page.offsetLeft + (props.field.x / exitingPageDims.x) * width;
    const pageY =
      (props.field.y / exitingPageDims.y) * height +
      props.field.page * height -
      8;

    const pageWidth = (props.field.w / exitingPageDims.x) * width;
    const pageHeight = (props.field.h / exitingPageDims.y) * height;

    setCoords({
      pageX: pageX,
      pageY: pageY,
      pageHeight: pageHeight,
      pageWidth: pageWidth,
      width: width,
      height: height,
    });

    setCoords((prevCoords) => {
      if (
        prevCoords.pageX !== pageX ||
        prevCoords.pageY !== pageY ||
        prevCoords.pageWidth !== pageWidth ||
        prevCoords.pageHeight !== pageHeight
      ) {
        return { pageX, pageY, pageWidth, pageHeight, width, height };
      }
      return prevCoords;
    });
  }, [props.field, props.pagesHeight]);

  useEffect(() => {
    props.field.existingField ? calculateExitingCoords() : calculateCoords();
  }, [props.field]);

  useEffect(() => {
    const onResize = () => {
      props.field.existingField ? calculateExitingCoords() : calculateCoords();
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [calculateCoords]);

  useEffect(() => {
    const $page = document.querySelector(
      `.react-pdf__Page[data-page-number="${props.field.page + 1}"]`,
    );

    if (!$page) return;

    const observer = new MutationObserver(() => {
      props.field.existingField ? calculateExitingCoords() : calculateCoords();
    });

    observer.observe($page, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [props.field]);

  /**
   * True once this signer has already placed their saved image on a field of
   * the same type, anywhere in the request. Until then every click has to be
   * confirmed in the "Use your saved signature" dialog; afterwards the
   * remaining fields fill straight away.
   *
   * The field being clicked needs no exclusion: it is always empty by the time
   * this decides anything, since a filled one opens the pad instead. Excluding
   * it by id would be wrong anyway — ids are handed out per file, so they
   * collide across documents.
   */
  const hasAlreadyPlaced = (type: "signature" | "initials") =>
    (props.state.signRequest.files ?? []).some((file: RequestFile) =>
      (file.fields ?? []).some(
        (other: FileField) =>
          other.type === type &&
          other.signerIndex === props.signer?.id &&
          isTruthy(other.value),
      ),
    );

  const getFields = () => {
    const isDisabled = props.disableFields === false;
    switch (props.field.type) {
      case "signature":
        return (
          <SignatureFields
            coords={coords}
            field={props.field}
            signImage={props.signImage}
            savedImageAlreadyPlaced={hasAlreadyPlaced("signature")}
            currentReviewer={props.currentReviewer}
            frameX={props.frameX}
            frameY={props.frameY}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            currentFieldOnFocus={props.currentFieldOnFocus}
            setCurrentFieldOnFocus={props.setCurrentFieldOnFocus}
            moveToFieldLocation={props.moveToFieldLocation}
            signingJourneyOn={props.signingJourneyOn}
            setLoading={props.setLoading}
            disableFields={isDisabled}
          />
        );
      case "initials":
        return (
          <SignatureFields
            coords={coords}
            field={props.field}
            frameX={props.frameX}
            currentReviewer={props.currentReviewer}
            frameY={props.frameY / 1.5}
            initialsImage={props.initialsImage}
            savedImageAlreadyPlaced={hasAlreadyPlaced("initials")}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            isInitialsPad={true}
            currentFieldOnFocus={props.currentFieldOnFocus}
            setCurrentFieldOnFocus={props.setCurrentFieldOnFocus}
            moveToFieldLocation={props.moveToFieldLocation}
            signingJourneyOn={props.signingJourneyOn}
            setLoading={props.setLoading}
            disableFields={isDisabled}
          />
        );
      case "name":
      case "email":
      case "date_signed":
      case "text":
      case "company_name":
      case "title":
        return (
          <CustomTextFieldsElement
            coords={coords}
            field={props.field}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            signer={props.signer}
            currentReviewer={props.currentReviewer}
            currentFieldOnFocus={props.currentFieldOnFocus}
            setCurrentFieldOnFocus={props.setCurrentFieldOnFocus}
            moveToFieldLocation={props.moveToFieldLocation}
            signingJourneyOn={props.signingJourneyOn}
            disableFields={isDisabled}
          />
        );
      case "drop_down":
        return (
          <CustomDropDownFieldElement
            coords={coords}
            field={props.field}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            currentReviewer={props.currentReviewer}
            currentFieldOnFocus={props.currentFieldOnFocus}
            setCurrentFieldOnFocus={props.setCurrentFieldOnFocus}
            moveToFieldLocation={props.moveToFieldLocation}
            signingJourneyOn={props.signingJourneyOn}
            disableFields={isDisabled}
          />
        );
      case "radio_button":
        return (
          <CustomRadioFieldElement
            coords={coords}
            field={props.field}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            disableFields={isDisabled}
          />
        );
      case "date":
        return (
          <CustomDateFieldElement
            coords={coords}
            field={props.field}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            disableFields={isDisabled}
            currentReviewer={props.currentReviewer}
          />
        );
      case "checkbox":
        return (
          <CustomCheckBoxFieldsElement
            coords={coords}
            field={props.field}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            disableFields={isDisabled}
            currentReviewer={props.currentReviewer}
          />
        );
      case "attachment":
        return (
          <CustomAttachmentFieldElement
            coords={coords}
            field={props.field}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            disableFields={isDisabled}
            currentReviewer={props.currentReviewer}
          />
        );
    }
  };

  const getHeight = (field: any) => {
    const styleSize = field.styles?.size ?? 16;
    if (coords.pageHeight < styleSize) {
      return styleSize;
    }
    return coords.pageHeight;
  };

  return (
    <Box
      key={coords.pageX + coords.pageX + coords.pageHeight + coords.pageWidth}
      sx={{
        position: "absolute",
        left: coords.pageX,
        top: coords.pageY,
        width: coords.pageWidth,

        height: coords.pageHeight * ry,
        cursor: "pointer",
        zIndex: 20,
        borderRadius: "3px",
        display: "flex",
        flexDirection: "column",
      }}
      ref={$el}
      id={props.field.id}
    >
      <Box
        height={getHeight(props.field)}
        minHeight={coords.pageHeight}
        sx={{ flexShrink: 0 }}
      >
        {getFields()}
      </Box>

      {props.isCurrentField &&
        !props.signingJourneyOn &&
        !props.currentReviewer?.email &&
        (() => {
          const totalFields = fieldsRemaining(
            props.state.signRequest.files,
          ).totalFields;
          const isPrevDisabled = props.currentFieldOnFocus <= 1;
          const isNextDisabled = props.currentFieldOnFocus >= totalFields;
          const isMobile = !!props.isMobileScreen;
          const pillHeight = isMobile ? 18 : 24;
          const iconSize = isMobile ? 12 : 16;
          const counterFontSize = isMobile ? "9px" : "11px";
          const spaceAbove =
            ((props.field.fieldPageY ?? 0) / 100) * (coords.height || 0);
          const placeAbove = spaceAbove >= pillHeight + 8;
          const arrowBoxSx = (disabled: boolean) => ({
            width: pillHeight,
            height: pillHeight,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            cursor: disabled ? "default" : "pointer",
            color: disabled ? "#bdbdbd" : theme.palette.text.primary,
            transition: "background-color 0.15s ease",
            "&:hover": {
              backgroundColor: disabled ? "transparent" : "#F0F0F0",
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.text.primary}`,
              outlineOffset: "1px",
            },
          });
          return (
            <Box
              sx={{
                position: "absolute",
                ...(placeAbove
                  ? { bottom: "100%", mb: "4px" }
                  : { top: "100%", mt: "4px" }),
                left: "50%",
                transform: "translateX(-50%)",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "2px" : "4px",
                height: pillHeight,
                px: isMobile ? "2px" : "4px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E0E0E0",
                borderRadius: "999px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                zIndex: 30,
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              <Box
                role="button"
                aria-label="Previous field"
                aria-disabled={isPrevDisabled}
                tabIndex={isPrevDisabled ? -1 : 0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isPrevDisabled)
                    props.moveToFieldLocation(props.currentFieldOnFocus - 1);
                }}
                onKeyDown={(e) => {
                  if (!isPrevDisabled && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    e.stopPropagation();
                    props.moveToFieldLocation(props.currentFieldOnFocus - 1);
                  }
                }}
                sx={arrowBoxSx(isPrevDisabled)}
                id={`signer-field-${props.field.name}-back-btn`}
              >
                <ChevronLeftRoundedIcon sx={{ fontSize: iconSize }} />
              </Box>
              <Typography
                sx={{
                  fontSize: counterFontSize,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  lineHeight: 1,
                  px: isMobile ? "1px" : "2px",
                }}
              >
                {props.currentFieldOnFocus}
                <Box
                  component="span"
                  sx={{ color: "#bdbdbd", mx: "1px" }}
                >
                  /
                </Box>
                {totalFields}
              </Typography>
              <Box
                role="button"
                aria-label="Next field"
                aria-disabled={isNextDisabled}
                tabIndex={isNextDisabled ? -1 : 0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isNextDisabled)
                    props.moveToFieldLocation(props.currentFieldOnFocus + 1);
                }}
                onKeyDown={(e) => {
                  if (!isNextDisabled && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    e.stopPropagation();
                    props.moveToFieldLocation(props.currentFieldOnFocus + 1);
                  }
                }}
                sx={arrowBoxSx(isNextDisabled)}
                id={`signer-field-${props.field.name}-next-btn`}
              >
                <ChevronRightRoundedIcon sx={{ fontSize: iconSize }} />
              </Box>
            </Box>
          );
        })()}
    </Box>
  );
};

export default ViewFields;
